<template>
  <main
    v-style:bgv="{ md: `#fff url(${backgroundImage.src.md}) no-repeat top center/cover` }"
    class="md:py-16"
  >
    <div
      v-style="{
        h: { sm: '100vw', md: 'auto' },
        bgv: { sm: `#fff url(${backgroundImage.src.sm}) no-repeat center/cover`, md: 'none' },
      }"
      class="text-center container <md:flex <md:items-end <md:center <md:py-6"
    >
      <h1 class="bg-white p-2 title-2 uppercase i-block">
        {{ headerTitle }}
      </h1>
    </div>
    <div class="text-center container">
      <p class="mt-6 bg-white p-2 i-block">
        {{ introBody }}
      </p>
    </div>
    <div v-style:w="{ md: '38rem' }" class="mx-4 my-8 md:mx-a">
      <base-form
        v-if="!showResults"
        aria-labelledby="form-heading"
        class="bg-white py-6 space-y-4 md:p-8 md:shadow-sm"
        :form
        @submit="handleSubmit"
      >
        <h2
          ref="formHeadingRef"
          id="form-heading"
          class="title-4 uppercase <md:text-center md:title-3"
          tabindex="-1"
        >
          {{ formSection.title }}
        </h2>
        <p class="<md:text-center">
          {{ formSection.intro }}
        </p>
        <vf-form-field
          v-slot="{ attrs }"
          :hint="formSection.inputProductSkuHint"
          name="productSku"
          :rule="[
            ...(!form.productSku && !form.productName
              ? [validateRequired('', formSection.errorMessageProductCodeOrNameMissing)]
              : []),
            ...(form.productSku ? [validateMinLength(8), validateMaxLength(8)] : [])]"
        >
          <vf-input v-model.trim="form.productSku" v-bind="attrs" :label="formSection.inputProductSkuLabel">
            <template #after>
              <vf-toggletip placement="top-end">
                <template #trigger>
                  <span class="sr-only">{{ formSection.inputProductSkuLabel }}</span>
                  <vf-icon name="info" size="md" />
                </template>
                {{ formSection.inputProductSkuTooltip }}
              </vf-toggletip>
            </template>
          </vf-input>
        </vf-form-field>
        <vf-form-field
          v-slot="{ attrs }"
          name="productName"
          :rule="!form.productSku && !form.productName
            ? [validateRequired('', formSection.errorMessageProductCodeOrNameMissing)]
            : []"
        >
          <vf-input v-model.trim="form.productName" v-bind="attrs" :label="formSection.inputProductNameLabel">
            <template #after>
              <vf-toggletip placement="top-end">
                <template #trigger>
                  <span class="sr-only">{{ formSection.inputProductNameLabel }}</span>
                  <vf-icon name="info" size="md" />
                </template>
                {{ formSection.inputProductNameTooltip }}
              </vf-toggletip>
            </template>
          </vf-input>
        </vf-form-field>
        <vf-form-field v-slot="{ attrs }" name="productDateCode">
          <vf-input
            v-model.trim="form.productDateCode"
            v-bind="attrs"
            :disabled="!form.productSku && !form.productName"
            :label="formSection.inputProductDateCodeLabel"
          >
            <template #after>
              <vf-toggletip
                placement="top-end"
                :props-trigger="{ disabled: !form.productSku && !form.productName }"
              >
                <template #trigger>
                  <span class="sr-only">{{ formSection.inputProductDateCodeLabel }}</span>
                  <vf-icon name="info" size="md" />
                </template>
                {{ formSection.inputProductDateCodeTooltip }}
              </vf-toggletip>
            </template>
          </vf-input>
        </vf-form-field>
        <vf-button class="w-full" type="submit">
          {{ formSection.confirm }}
        </vf-button>
      </base-form>

      <div v-else class="f-col items-center">
        <div v-style:max-h="{ md: '28rem' }" class="full overflow-y-auto md:shadow-lg">
          <table ref="tableRef" class="w-full bg-white" tabindex="-1">
            <caption class="w-full bg-brand-1 p-4 text-left c-white uppercase">
              {{ results.length }} {{ $pluralize(resultSection.resultsFound, results.length) }}
            </caption>
            <tr
              v-for="({ name, sku, url }, key) in results"
              :key
              class="text-sm"
              :class="{ 'bg-grey-80': key % 2 }"
            >
              <td class="p-4 pr-2">
                {{ sku }}
              </td>
              <td class="px-2 py-4">
                {{ name }}
              </td>
              <td class="pr-2 text-right">
                <vf-button size="xs" :to="url">
                  <span class="<md:hidden">{{ resultSection.viewPdf }}</span>
                  <span class="md:hidden">PDF</span>
                  <span class="sr-only"> - {{ name }}</span>
                </vf-button>
              </td>
            </tr>
          </table>
        </div>
        <vf-button class="mx-a mt-6" @click="handleReset">
          <vf-icon name="refresh" size="md" />
          {{ resultSection.reset }}
        </vf-button>
      </div>
    </div>
  </main>
</template>

<script lang="ts" setup>
import type { CloudinaryAsset, DeclarationOfConformityAsset } from '#types/cloudinaryAsset'

const cloudinaryUrlPrefix = 'https://assets.vans.eu'

const { data: assets } = await useFetch(`${cloudinaryUrlPrefix}/any/list/declaration-of-conformity.json`, {
  transform: ({ resources }: { resources: CloudinaryAsset[] }): DeclarationOfConformityAsset[] =>
    resources.map(({ format, public_id, resource_type, type, version }) => {
    /**
     * filename pattern
     * DOC_SKU_SEASON_DATECODE_NAME_...
     *
     * @example
     * DOC_VN00007E_SP19_XXXX_SQUARED-OFF
     * DOC_VN000LC0_SS24_0923_SPICOLI-4
     *
     */
      const parts = public_id.replace(/-/g, ' ').split('_')
      const [prefix, sku, , datecode, name] = parts

      const url = `${cloudinaryUrlPrefix}/${resource_type}/${type}/v${version}/${public_id}.${format}`

      return { datecode, name, prefix, sku, url }
    })
})

if (!assets.value) throw createError({ fatal: true })

const { data, error } = await useApi().internal.getStaticPage('declaration-of-conformity')
if (!data.value) throw createError({ ...error.value!, fatal: true })
const { backgroundImage, formSection, headerTitle, introBody, resultSection } = data.value

const results = ref<DeclarationOfConformityAsset[]>([])
const showResults = ref(false)

const { validateMaxLength, validateMinLength, validateRequired } = useLocalizedValidators()
const formHeadingRef = useTemplateRef('formHeadingRef')
const tableRef = useTemplateRef('tableRef')

const form = reactive({
  productName: '',
  productSku: '',
  productDateCode: ''
})

const resetForm = () => {
  form.productName = ''
  form.productSku = ''
  form.productDateCode = ''
}

const handleReset = () => {
  resetForm()
  results.value = []
  showResults.value = false
  nextTick(() => formHeadingRef.value?.focus())
}

const handleSubmit = () => {
  if (assets.value === null) assets.value = []
  results.value = assets.value.filter(({ datecode, name, prefix, sku }) => prefix.toLowerCase() === 'doc'
    && (!form.productSku || sku.toLowerCase().startsWith(form.productSku.toLowerCase()))
    && (!form.productName || name.toLowerCase().includes(form.productName.toLowerCase()))
    && (!form.productDateCode || datecode.toLowerCase().includes(form.productDateCode.toLowerCase())))
  showResults.value = true
  nextTick(() => tableRef.value?.focus())
}
</script>
